/**
 * Core Web Vitals Monitoring Utility
 * 
 * Tracks and reports Core Web Vitals metrics to Google Analytics.
 * Monitors LCP, CLS, TTFB, and INP for performance optimization.
 * 
 * Note: FID (First Input Delay) has been deprecated in web-vitals v4
 * and replaced by INP (Interaction to Next Paint) which is more comprehensive.
 */

import { onCLS, onLCP, onTTFB, onINP, type Metric } from 'web-vitals';

interface WebVitalMetric {
  name: 'LCP' | 'CLS' | 'TTFB' | 'INP';
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  delta: number;
  id: string;
  navigationType: string;
  pathname: string;
  timestamp: number;
}

type MetricCallback = (metric: WebVitalMetric) => void;

/**
 * Core Web Vitals Monitor class
 */
export class CoreWebVitalsMonitor {
  private static instance: CoreWebVitalsMonitor;
  private metrics: Map<string, WebVitalMetric> = new Map();
  private callbacks: MetricCallback[] = [];

  private constructor() {}

  /**
   * Get singleton instance
   */
  static getInstance(): CoreWebVitalsMonitor {
    if (!CoreWebVitalsMonitor.instance) {
      CoreWebVitalsMonitor.instance = new CoreWebVitalsMonitor();
    }
    return CoreWebVitalsMonitor.instance;
  }

  /**
   * Initialize Core Web Vitals monitoring
   * Sets up listeners for all metrics
   * Note: FID is deprecated in web-vitals v4, replaced by INP
   */
  init(callback?: MetricCallback): void {
    if (callback) {
      this.callbacks.push(callback);
    }

    // Monitor LCP (Largest Contentful Paint)
    onLCP((metric) => this.handleMetric(metric), { reportAllChanges: false });

    // Monitor CLS (Cumulative Layout Shift)
    onCLS((metric) => this.handleMetric(metric), { reportAllChanges: false });

    // Monitor TTFB (Time to First Byte)
    onTTFB((metric) => this.handleMetric(metric));

    // Monitor INP (Interaction to Next Paint) - replaces FID
    onINP((metric) => this.handleMetric(metric));
  }

  /**
   * Handle metric collection
   */
  private handleMetric(metric: Metric): void {
    const webVitalMetric: WebVitalMetric = {
      name: metric.name as WebVitalMetric['name'],
      value: metric.value,
      rating: metric.rating,
      delta: metric.delta,
      id: metric.id,
      navigationType: metric.navigationType,
      pathname: window.location.pathname,
      timestamp: Date.now(),
    };

    // Store metric
    this.metrics.set(metric.name, webVitalMetric);

    // Report to callbacks
    this.callbacks.forEach(cb => cb(webVitalMetric));

    // Report to analytics
    this.reportMetric(webVitalMetric);
  }

  /**
   * Report metric to Google Analytics
   */
  reportMetric(metric: WebVitalMetric): void {
    // Check if gtag is available
    if (typeof window !== 'undefined' && (window as Window & { gtag?: (...args: unknown[]) => void }).gtag) {
      (window as unknown as Window & { gtag: (...args: unknown[]) => void }).gtag('event', metric.name, {
        event_category: 'Web Vitals',
        event_label: metric.id,
        value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
        metric_rating: metric.rating,
        metric_delta: Math.round(metric.delta),
        page_path: metric.pathname,
        navigation_type: metric.navigationType,
        non_interaction: true,
      });
    }

    // Also log to console in development
    if (import.meta.env.DEV) {
      console.log(`[Core Web Vitals] ${metric.name}:`, {
        value: metric.value,
        rating: metric.rating,
        pathname: metric.pathname,
      });
    }
  }

  /**
   * Get all collected metrics
   */
  getMetrics(): WebVitalMetric[] {
    return Array.from(this.metrics.values());
  }

  /**
   * Get a specific metric by name
   */
  getMetric(name: WebVitalMetric['name']): WebVitalMetric | undefined {
    return this.metrics.get(name);
  }

  /**
   * Check if all metrics are in "good" range
   */
  isPerformanceGood(): boolean {
    const metrics = this.getMetrics();
    
    if (metrics.length === 0) {
      return false;
    }

    return metrics.every(metric => metric.rating === 'good');
  }

  /**
   * Get performance summary
   */
  getSummary(): {
    allGood: boolean;
    metrics: Record<string, { value: number; rating: string }>;
  } {
    const metrics = this.getMetrics();
    const summary: Record<string, { value: number; rating: string }> = {};

    metrics.forEach(metric => {
      summary[metric.name] = {
        value: metric.value,
        rating: metric.rating,
      };
    });

    return {
      allGood: this.isPerformanceGood(),
      metrics: summary,
    };
  }

  /**
   * Add a callback for metric updates
   */
  onMetric(callback: MetricCallback): void {
    this.callbacks.push(callback);
  }

  /**
   * Clear all metrics (useful for testing)
   */
  clear(): void {
    this.metrics.clear();
  }
}

/**
 * Helper function to initialize Core Web Vitals monitoring
 */
export function initCoreWebVitals(callback?: MetricCallback): void {
  const monitor = CoreWebVitalsMonitor.getInstance();
  monitor.init(callback);
}

/**
 * Helper function to get current metrics
 */
export function getCoreWebVitals(): WebVitalMetric[] {
  const monitor = CoreWebVitalsMonitor.getInstance();
  return monitor.getMetrics();
}

/**
 * Helper function to check if performance is good
 */
export function isPerformanceGood(): boolean {
  const monitor = CoreWebVitalsMonitor.getInstance();
  return monitor.isPerformanceGood();
}
